import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="28961" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-black uppercase tracking-widest text-foreground/80 hover:text-primary transition-colors font-sans group py-2 block">
                {item.label}
                <span className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 origin-center transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="text-muted-foreground p-8 font-mono text-sm tracking-widest">LOADING COMMAND CENTER...</div>;
  }
  return <nav className="flex flex-col gap-6 p-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/10 pb-4 last:border-0">
          <Link to={item.href || "#"} className="text-xl font-black uppercase tracking-wider text-foreground hover:text-primary transition-colors block font-sans" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
      <div className="mt-8 pt-4 border-t border-border/20">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest h-12 text-base rounded-sm" asChild>
          <Link to="/HomePage" onClick={onClose}>
            Shop Our Gear
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function SiteForgeNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="command-header" className="dark bg-background text-foreground relative z-50">
      <header className={`fixed top-0 left-0 right-0 w-full transition-all duration-500 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/20 shadow-lg shadow-black/40 py-3" : "bg-background border-transparent py-5"}`}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex-shrink-0 z-50">
            <Link to="/HomePage" className="block group">
              <span className="text-primary font-black uppercase tracking-widest text-xl transition-opacity duration-300 group-hover:opacity-90">
                Paniolo Trails LLC
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-10">
            <MenuProvider menu_id="98">
              <DesktopMenuConsumer />
            </MenuProvider>

            <div className="flex items-center gap-6">
              <CartIcon variant="ghost" size="icon" />
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-widest text-xs font-bold px-6 h-10 rounded-sm shadow-[0_0_15px_rgba(179,0,0,0.3)] hover:shadow-[0_0_25px_rgba(179,0,0,0.5)] transition-all duration-300 border border-primary/20">
                <Link to="/HomePage">Shop Our Gear</Link>
              </Button>
            </div>
          </div>

          <div className="md:hidden flex items-center z-50 gap-3">
            <CartIcon variant="ghost" size="icon" className="text-foreground" />
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-accent hover:text-accent-foreground rounded-sm" aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        <div className={`fixed inset-0 bg-background/95 backdrop-blur-xl z-40 md:hidden transition-all duration-300 ease-in-out flex flex-col ${isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-4"}`} style={{
        top: "0",
        paddingTop: "80px"
      }}>
          <div className="h-full overflow-y-auto">
            <MenuProvider menu_id="98">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>
      </header>
      <div className="h-20" />
    </section>;
}